import { AiseaApiFactory } from "@/api/aisea/apiFactory";
import isBoolean from "lodash/isBoolean";
import isObject from "lodash/isObject";
import isString from "lodash/isString";
import isNumber from "lodash/isNumber";
import mapKeys from "lodash/mapKeys";

const operationWorkflowApi = AiseaApiFactory.get("operationWorkflow");

class OperationWorkflowService {
  /**
   * APIでoperationのWorkflow一覧を取得する
   * @returns {Promise<Object|null>} 操作設定のオブジェクトまたはnull
   * @property {boolean} status - APIのリクエストが成功したかどうか
   * @property {number} code - レスポンスのステータスコード
   * @property {OperationWorkflow[]} data - レスポンスのデータ
   * @property {string} message - レスポンスのメッセージ
   * @throws {Error}
   */
  async getOperationWorkflows() {
    return await operationWorkflowApi.index();
  }

  // ResponseEnterpriseOperationWorkflow[] を返す
  validOperationWorkflows(
    operationWorkflows // :ResponseEnterpriseOperationWorkflow[]
  ) {
    if (!this.isValidOperationWorkflows(operationWorkflows)) return [];
    return operationWorkflows.filter(ow => this.isValidOperationWorkflow(ow));
  }

  // ResponseEnterpriseOperationWorkflowVessel[] を返す
  operationWorkflowVessels(
    operationWorkflows // :ResponseEnterpriseOperationWorkflow[]
  ) {
    return this.validOperationWorkflows(operationWorkflows)
      .map(ow => ow.ao_enterprise_workflow_vessels)
      .flat()
      .filter(owv => this.isValidOperationWorkflowVessel(owv));
  }

  // ResponseEnterpriseOperationWorkflowVesselDepartment[] を返す
  operationWorkflowVesselDepartments(
    operationWorkflowVessels // :ResponseEnterpriseOperationWorkflowVessel[]
  ) {
    if (!this.isValidOperationWorkflowVessels(operationWorkflowVessels)) {
      return [];
    }
    return operationWorkflowVessels
      .filter(owv => this.isValidOperationWorkflowVessel(owv))
      .map(ow => ow.ao_enterprise_workflow_vessel_departments)
      .flat()
      .filter(oWVD => this.isValidOperationWorkflowVesselDepartment(oWVD));
  }

  // ResponseEnterpriseOperationWorkflowVesselAccount[] を返す
  operationWorkflowVesselAccounts(
    operationWorkflowVessels // :ResponseEnterpriseOperationWorkflowVessel[]
  ) {
    if (!this.isValidOperationWorkflowVessels(operationWorkflowVessels)) {
      return [];
    }
    return operationWorkflowVessels
      .filter(owv => this.isValidOperationWorkflowVessel(owv))
      .map(ow => ow.ao_enterprise_workflow_vessel_accounts)
      .flat()
      .filter(oWVA => this.isValidOperationWorkflowVesselAccount(oWVA));
  }

  isValidOperationWorkflows(operationWorkflows) {
    return Array.isArray(operationWorkflows);
  }
  isValidOperationWorkflowVessels(operationWorkflowVessels) {
    return Array.isArray(operationWorkflowVessels);
  }

  // ResponseEnterpriseOperationWorkflow か判定
  // bool
  isValidOperationWorkflow(operationWorkflow) {
    const ow = operationWorkflow;
    if (!isObject(ow)) return false;
    return (
      isString(ow.id) &&
      isString(ow.ao_enterprise_id) &&
      Array.isArray(ow.ao_enterprise_workflow_vessels) &&
      Array.isArray(ow.ao_enterprise_workflow_transactions) &&
      Array.isArray(ow.ao_enterprise_workflow_statuses) &&
      Array.isArray(ow.ao_enterprise_workflow_forms) &&
      isString(ow.created_at) &&
      isString(ow.updated_at)
    );
  }

  /**
   * Validates a ResponseEnterpriseOperationWorkflowVessel object
   * @param {ResponseEnterpriseOperationWorkflowVessel} operationWorkflowVessel - The object to validate
   * @returns {boolean} - True if the object is valid, false otherwise
   */
  isValidOperationWorkflowVessel(operationWorkflowVessel) {
    const owv = operationWorkflowVessel;
    if (!isObject(owv)) return false;
    return (
      isString(owv.id) &&
      isString(owv.enterprise_vessel_id) &&
      Array.isArray(owv.ao_enterprise_workflow_vessel_accounts) &&
      Array.isArray(owv.ao_enterprise_workflow_vessel_departments) &&
      isString(owv.created_at) &&
      isString(owv.updated_at)
    );
  }

  /**
   * Validates a ResponseEnterpriseOperationWorkflowVesselDepartment object
   * @param {ResponseEnterpriseOperationWorkflowVesselDepartment} department - The object to validate
   * @returns {boolean} - True if the object is valid, false otherwise
   */
  isValidOperationWorkflowVesselDepartment(department) {
    const dep = department;
    if (!isObject(dep)) return false;
    return (
      isString(dep.id) &&
      isString(dep.name) &&
      isString(dep.created_at) &&
      isString(dep.updated_at)
    );
  }
  // isValidOperationWorkflowVesselAccount

  /**
   * Validates a ResponseEnterpriseOperationWorkflowVesselAccount object
   * @param {ResponseEnterpriseOperationWorkflowVesselAccount} account - The object to validate
   * @returns {boolean} - True if the object is valid, false otherwise
   */
  isValidOperationWorkflowVesselAccount(account) {
    const acc = account;
    if (!isObject(acc)) return false;
    return (
      isString(acc.id) &&
      isBoolean(acc.is_responsible) &&
      isBoolean(acc.creatable) &&
      isBoolean(acc.readable) &&
      isBoolean(acc.updatable) &&
      isBoolean(acc.deletable) &&
      isString(acc.created_at) &&
      isString(acc.updated_at) &&
      isString(acc.account_id)
    );
  }

  /**
   * Checks if an object is of type ResponseEnterpriseOperationWorkflowForm
   * @param {Object} obj - The object to check
   * @returns {boolean} - True if the object is of type ResponseEnterpriseOperationWorkflowForm, false otherwise
   */
  isOperationWorkflowForm(obj) {
    return (
      isObject(obj) &&
      isString(obj.id) &&
      isNumber(obj.sort_order) &&
      isString(obj.name) &&
      isString(obj.form_id) &&
      isString(obj.created_at) &&
      isString(obj.updated_at)
    );
  }

  /**
   * Checks if an object is of type ResponseEnterpriseOperationWorkflowStatus
   * @param {Object} obj - The object to check
   * @returns {boolean} - True if the object is of type ResponseEnterpriseOperationWorkflowStatus, false otherwise
   */
  isResponseEnterpriseOperationWorkflowStatus(obj) {
    return (
      isObject(obj) &&
      isString(obj.id) &&
      isString(obj.workflow_status_id) &&
      Array.isArray(obj.ao_enterprise_workflow_status_accounts) &&
      Array.isArray(obj.ao_enterprise_workflow_status_forms) &&
      isString(obj.created_at) &&
      isString(obj.updated_at)
    );
  }

  /**
   * Checks if an object is of type ResponseEnterpriseOperationWorkflowStatusAccount
   * @param {Object} obj - The object to check
   * @returns {boolean} - True if the object is of type ResponseEnterpriseOperationWorkflowStatusAccount, false otherwise
   */
  isResponseEnterpriseOperationWorkflowStatusAccount(obj) {
    return (
      isObject(obj) &&
      isString(obj.id) &&
      isBoolean(obj.deletable) &&
      isString(obj.account_id) &&
      isString(obj.created_at) &&
      isString(obj.updated_at)
    );
  }

  /**
   * Checks if an object is of type ResponseEnterpriseOperationWorkflowTransaction
   * @param {Object} obj - The object to check
   * @returns {boolean} - True if the object is of type ResponseEnterpriseOperationWorkflowTransaction, false otherwise
   */
  isResponseEnterpriseOperationWorkflowTransaction(obj) {
    return (
      isObject(obj) &&
      isString(obj.id) &&
      isString(obj.workflow_transaction_id) &&
      Array.isArray(obj.ao_enterprise_workflow_transaction_accounts) &&
      isString(obj.created_at) &&
      isString(obj.updated_at)
    );
  }

  /**
   * Checks if an object is of type ResponseEnterpriseOperationWorkflowTransactionAccount
   * @param {Object} obj - The object to check
   * @returns {boolean} - True if the object is of type ResponseEnterpriseOperationWorkflowTransactionAccount, false otherwise
   */
  isResponseEnterpriseOperationWorkflowTransactionAccount(obj) {
    return (
      isObject(obj) &&
      isString(obj.id) &&
      isString(obj.account_id) &&
      isString(obj.created_at) &&
      isString(obj.updated_at)
    );
  }

  /**
   * Checks if the given account has permission for the operation workflow transaction.
   *
   * @param {Object} operationWorkflowTransaction - The operation workflow transaction object.
   * @param {string} accountId - The ID of the account to check permission for.
   * @returns {boolean} - Returns true if the account has permission, false otherwise.
   */
  hasAccountOperationWorkflowTransactionPermission(
    operationWorkflowTransaction,
    accountId
  ) {
    const isOperationWorkflowTransaction = this.isResponseEnterpriseOperationWorkflowTransaction(
      operationWorkflowTransaction
    );
    if (!isOperationWorkflowTransaction) return false;
    if (!isString(accountId)) return false;

    return operationWorkflowTransaction.ao_enterprise_workflow_transaction_accounts
      .filter(workflowTransactionAccount =>
        this.isResponseEnterpriseOperationWorkflowTransactionAccount(
          workflowTransactionAccount
        )
      )
      .some(account => account.account_id === accountId);
  }

  /**
   * ResponseOperationWorkflowVessel を id でマップしたオブジェクト を返す
   *
   * @param {ResponseOperationWorkflowVessel[]} operationWorkflowVessels - マップする対象のオブジェクトの配列
   * @returns {Object} - キーが"id"となるマップされたオブジェクト
   */
  operationWorkflowVesselMap(operationWorkflowVessels) {
    return mapKeys(operationWorkflowVessels, "id");
  }

  /**
   * ResponseOperationWorkflowVesselDepartment を id でマップしたオブジェクト を返す
   *
   * @param {ResponseOperationWorkflowVesselDepartment[]} operationWorkflowVesselDepartments - マッピングするオブジェクトの配列
   * @returns {{Object}} - キーが"id"となるマッピングされたオブジェクト
   */
  operationWorkflowVesselDepartmentMap(operationWorkflowVesselDepartments) {
    return mapKeys(operationWorkflowVesselDepartments, "id");
  }

  operationWorkflowVesselAccountMap(operationWorkflowVesselAccounts) {
    return mapKeys(operationWorkflowVesselAccounts, "id");
  }
}

export default new OperationWorkflowService();
